import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useDashboardMetricsQuery } from './useDashboardMetricsQuery';
import { queryClient, queryKeys } from '../lib/queryClient';

/**
 * Custom hook for dashboard metrics with React Query caching.
 * Subscribes to Supabase realtime and invalidates the cache on changes.
 */
export const useDashboardMetrics = (userId) => {
  const result = useDashboardMetricsQuery(userId);

  useEffect(() => {
    if (!userId) return;

    const invalidate = () =>
      queryClient?.invalidateQueries({ queryKey: queryKeys?.dashboardMetrics(userId) });

    const workoutChannel = supabase?.channel(`workout_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'workout_logs', filter: `user_id=eq.${userId}` },
        invalidate
      )?.subscribe();

    const nutritionChannel = supabase?.channel(`nutrition_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'nutrition_logs', filter: `user_id=eq.${userId}` },
        invalidate
      )?.subscribe();

    const sleepChannel = supabase?.channel(`sleep_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sleep_logs', filter: `user_id=eq.${userId}` },
        invalidate
      )?.subscribe();

    const waterChannel = supabase?.channel(`water_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'water_intake_logs', filter: `user_id=eq.${userId}` },
        invalidate
      )?.subscribe();

    const profileChannel = supabase?.channel(`profile_changes_${userId}`)?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${userId}` },
        invalidate
      )?.subscribe();

    return () => {
      supabase?.removeChannel(workoutChannel);
      supabase?.removeChannel(nutritionChannel);
      supabase?.removeChannel(sleepChannel);
      supabase?.removeChannel(waterChannel);
      supabase?.removeChannel(profileChannel);
    };
  }, [userId]);

  return result;
};

/**
 * Custom hook for real-time activity feed
 */
export const useActivityFeed = (userId) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivities = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('activity_feed')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);
      if (fetchError) throw fetchError;
      setActivities(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    const channel = supabase?.channel('activity_feed_changes')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'activity_feed', filter: `user_id=eq.${userId}` },
        () => fetchActivities()
      )?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [userId]);

  return { activities, loading, error, refetch: fetchActivities };
};